import { useEffect, useRef } from 'react';

/**
 * Landlord error fallback UI, rendered by the shared AnalyticsErrorBoundary
 * (packages/analytics/src/error-boundary.tsx) when an uncaught render error
 * is caught at the app root. Deliberately does NOT render error.message/stack
 * to the end user.
 */

interface LandlordErrorFallbackProps {
  error: Error;
  onReset: () => void;
}

// error is kept in the props contract (callers pass it) but deliberately unused: this
// fallback never renders error.message/stack.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function LandlordErrorFallback({ error: _error, onReset }: LandlordErrorFallbackProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Move focus to the heading on mount so screen-reader/keyboard users are
  // told the app just crashed instead of discovering it only if they
  // re-explore the page (FM2).
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  function handleReset() {
    onReset();
    window.location.href = '/landlord/dashboard';
  }

  return (
    <div
      data-testid="error-fallback-page"
      role="alert"
      aria-live="assertive"
      className="min-h-screen flex items-center justify-center bg-accent/50 p-6"
    >
      <div className="bg-card rounded-lg border border-border max-w-lg w-full p-8 text-center">
        <div className="text-6xl mb-4">⚠️</div>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-2xl font-bold mb-3 text-foreground outline-none"
        >
          Something went wrong
        </h1>
        <p className="text-base text-muted-foreground mb-6">
          We encountered an unexpected error. This has been logged and we'll look into it.
        </p>
        <button
          data-testid="error-boundary-reset"
          onClick={handleReset}
          className="w-full px-5 py-2.5 text-sm font-medium text-primary-foreground bg-primary border-none rounded-md cursor-pointer hover:bg-primary/90 min-h-[44px]"
        >
          Return to Dashboard
        </button>
      </div>
    </div>
  );
}
